/**
 * THE WORK — what this piece of work is, where it stands, and what it is
 * waiting for.
 *
 * EVERY PROFESSIONAL CLAIM ON THIS PAGE IS THE SERVER'S. The band's tone, word
 * and note; the state ledger; the figures and their formatting; the facts and
 * their readings; the conclusions, their wording and their tone — all of it is
 * computed in `@leveri/work-os` and arrives decided. This file arranges them.
 * It contains no branch that decides what state anything is in.
 *
 * THE ENGINE ACCEPTS NOTHING. Every conclusion carries the governance status
 * the engine recorded (PROPOSED), and beside it the REVIEW a person with
 * review authority made — accept, adjust, reject, request evidence (R7,
 * ADR-0014). Whether this viewer may review, and every word about a review,
 * is the server's.
 *
 * WHAT IS DELIBERATELY ABSENT: an "I don't have this" answer, a progress
 * stage, an elapsed time. Each would state something the professional record
 * does not hold.
 */
import { AskClient } from "./askClient.js";
import { ConfirmClientResponses } from "./confirmClient.js";
import { useState } from "react";
import type { ConclusionRow, WorkFile } from "@leveri/contracts";
import {
  Band, Citation, Figure, Label, Marker, RuledList, Section, SectionHead, StateText, Statement, FileTitle, markerTone,
} from "../../file/index.js";
import { AnswerRequest, DeclareNotAvailable } from "./answer.js";
import { CorrectLine } from "./correctLine.js";
import { NeedsAttention } from "./attention.js";
import { ReviewControl, ReviewPosition } from "./review.js";
import { IntentSection } from "./intent.js";
import { ProposedReading } from "./investigation.js";
import { StartSection } from "./start.js";
import { PreparationBooks, PreparationDecide, PreparationLead } from "./preparation.js";
import { AssignControl } from "../assign.js";
import { usePlaceWords } from "../shell.js";
import { ChatBox } from "../chat.js";

/**
 * THE TWO QUESTIONS THAT ARE KEPT APART.
 *
 * These are the engine's own `questionKind` values, carried verbatim on
 * `ConclusionRow.questionKind`. The contract has no grouping key, so the
 * screen addresses the two the direction requires be kept visibly separate —
 * explaining a difference and supporting a treatment are different
 * professional acts, and running them together is how one gets read as the
 * other. Everything else falls through to the general section, so no
 * conclusion the engine recorded can be dropped by a name this file does not
 * recognise.
 */
const RECONCILIATION_CAUSE = "RECONCILIATION_CAUSE";
const ACCOUNTING_TREATMENT = "ACCOUNTING_TREATMENT_SUPPORT";
/** ADR-0019 F — a preparation's two kinds of decision, each its own list. */
const CLASSIFICATION = "COUNTERPARTY_CLASSIFICATION";
const MAPPING = "ACCOUNT_MAPPING";

export interface WorkSectionProps {
  work: WorkFile;
  outcome: string | null;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function WorkSection({ work, outcome, onWork, onOutcome }: WorkSectionProps) {
  const place = usePlaceWords();
  const cause = work.conclusions.filter((c) => c.questionKind === RECONCILIATION_CAUSE);
  const treatment = work.conclusions.filter((c) => c.questionKind === ACCOUNTING_TREATMENT);
  const classifications = work.conclusions.filter((c) => c.questionKind === CLASSIFICATION);
  const mappings = work.conclusions.filter((c) => c.questionKind === MAPPING);
  const others = work.conclusions.filter((c) => ![RECONCILIATION_CAUSE, ACCOUNTING_TREATMENT, CLASSIFICATION, MAPPING].includes(c.questionKind));
  const preparation = work.preparation;
  // One review open at a time: one primary on the screen.
  const [reviewing, setReviewing] = useState<string | null>(null);
  const reviewProps = { work, reviewing, onReviewing: setReviewing, onWork, onOutcome };

  /**
   * THE ANSWER LEADS (owner-accepted design, 2026-10-09). When the server has
   * stated the cause in one line, that line is the page's headline; the
   * engine's summary sentence follows only when it says something else. Both
   * are the server's words — this file chooses their ORDER, never their content.
   */
  const headlines = work.explanations.map((e) => e.headline).filter((h): h is string => Boolean(h));
  const lead = headlines[0] ?? work.interpretation ?? null;
  const secondLine = headlines.length > 0 && work.interpretation && work.interpretation !== lead ? work.interpretation : null;
  const keyFigures = work.figures.filter((f) => !f.total || work.figures.length <= 3);

  return (
    <div>
      <FileTitle kicker={work.engagementTitle} meta={`${work.clientName} · ${work.periodText} · ${work.currency}`}>
        {work.title}
      </FileTitle>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {work.startedText && <span className="text-[13px] text-stone" data-testid="work-started">{work.startedText}</span>}
        <AssignControl objectKind="WORK" objectId={work.id} holder={work.assignee} onChanged={(assignee) => onWork({ ...work, assignee })} />
      </div>

      {/* THE ONE THING HAPPENING NOW. The tone, the word and the sentence all
          arrive from the projection's `band`; nothing here decides them. */}
      <Band tone={markerTone(work.band.tone)} className="mt-6 flex flex-wrap items-center gap-x-3.5 gap-y-1 px-5 py-3">
        <Marker tone={markerTone(work.band.tone)} />
        <span className="text-[15px] font-medium text-ink">{work.band.label}</span>
        <span className="text-[13px] text-stone">{work.band.note}</span>
      </Band>

      {/* R6.3/R6.5: shown only while the server says technical attention is open. */}
      <NeedsAttention work={work} onWork={onWork} onOutcome={onOutcome} />

      {/* ONE BOX, FOUR PLACES (0042): the box inside the work knows the job. Words are answered from its own file;
          files go to it, answering its one open request. Each person sees only their own earlier messages here. */}
      <div className="mt-6 max-w-[820px]" data-testid="work-chat">
        <ChatBox scope={{ workId: work.id }} placeholder="Ask about this work, or send what it is waiting for" onWork={onWork} />
      </div>

      {outcome && (
        <Section>
          <Label>What that did</Label>
          <p className="mt-1.5 text-sm text-ink">{outcome}</p>
        </Section>
      )}

      {work.status === "DRAFT" ? (
        <>
          {/* WHAT LEVERI UNDERSTOOD — before the work starts it is what the work is FOR. */}
          <IntentSection work={work} onWork={onWork} onOutcome={onOutcome} />
          <StartSection workId={work.id} clientId={work.clientId} start={work.start} onWork={onWork} onOutcome={onOutcome} />
        </>
      ) : (
        <>
          {/* ── 1. THE ANSWER, AND THE FIGURES IT IS ABOUT ─────────────────── */}
          {preparation && <PreparationLead preparation={preparation} />}
          {!preparation && (lead || work.figures.length > 0) && (
            <Section id="figures">
              {lead && <Statement>{lead}</Statement>}
              {secondLine && <p className="mt-3 text-[15px] text-ink-soft">{secondLine}</p>}
              {keyFigures.length > 0 && (
                <div className="mt-6 grid grid-cols-1 border-y border-rule sm:grid-cols-3">
                  {keyFigures.slice(0, 3).map((figure, index) => (
                    <div key={figure.key} className={`py-4 ${index > 0 ? "sm:border-l sm:border-rule-soft sm:pl-6" : ""}`}>
                      <div className="text-[12px] text-stone" title={figure.citation ?? undefined}>{figure.label}</div>
                      <div className="mt-1.5 font-mono text-[19px] text-ink [font-variant-numeric:tabular-nums]">{figure.text}</div>
                      {/* R8.1 — the server's sentence: where this stands against the engagement's materiality. */}
                      {figure.assessment && (
                        <p className="mt-1 text-[12px]" title={figure.assessment.band} data-testid={`materiality-${figure.key}`}>
                          <StateText tone={markerTone(figure.assessment.tone)}>{figure.assessment.label}</StateText>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {keyFigures.length > 3 && keyFigures.slice(3).map((figure) => (
                <Figure key={figure.key} label={figure.label} citation={figure.citation} value={figure.text} total={figure.total} />
              ))}
              {work.materiality ? (
                <p className="mt-3 text-[13px] text-stone">
                  Materiality: overall {work.materiality.overallText} · performance {work.materiality.performanceText} · clearly trivial {work.materiality.clearlyTrivialText}. Set by {work.materiality.setByName}, {work.materiality.setAtText}.
                </p>
              ) : work.figures.length > 0 ? (
                <p className="mt-3 text-[13px] text-stone">No materiality is set for this {place.engagement}, so differences are not stated against it.</p>
              ) : null}
              {work.figures.some((f) => f.assessment) && (
                <div className="mt-2 flex flex-col gap-1">
                  {work.figures.filter((f) => f.assessment).map((f) => (
                    <p key={f.key} className="text-[13px] text-stone">{f.label}: {f.assessment!.sentence}</p>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* ── 2. WHAT IS STILL NEEDED — it is why the file was opened ─────── */}
          {work.openRequests.length > 0 && (
            <Section id="open">
              <div className="flex flex-col gap-4">
                {work.openRequests.map((request) => (
                  // A requirement IS a separable object, which is why it is one
                  // of the only bordered things in the product.
                  <div key={request.requirementId} id={request.clarificationId ? `request-${request.clarificationId}` : undefined} className="scroll-mt-14 border border-rule bg-raised px-6 py-5">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <Label className="text-att-text">Still needed</Label>
                      <span title={request.raisedAt}><Citation>Raised {request.raisedAtText}</Citation></span>
                    </div>
                    <h3 className="mt-2 text-[16px] font-medium text-ink">{request.heading}</h3>
                    {request.context && <p className="mt-1.5 text-sm text-ink-soft">{request.context}</p>}
                    <p className="mt-1.5 max-w-[680px] text-sm text-ink">{request.questionPlain ?? request.question}</p>
                    {/* Said, not folded away: a tester read the closed "What exactly is needed" as empty. Shown only
                        when it says more than the line above. */}
                    {request.detail && request.detail !== (request.questionPlain ?? request.question) && (
                      <p className="mt-1.5 max-w-[680px] text-[13px] text-stone">{request.detail}</p>
                    )}
                    <AskClient workId={work.id} clientId={work.clientId} request={request} />
                    <ConfirmClientResponses
                      workId={work.id}
                      request={request}
                      others={work.openRequests.filter((o) => o.requirementId !== request.requirementId)}
                      onWork={onWork}
                      onOutcome={onOutcome}
                    />
                    <details className="mt-3">
                      <summary className="cursor-pointer list-none text-[13px] font-medium text-ink hover:underline">
                        I have it — answer with records
                      </summary>
                      <AnswerRequest
                        workId={work.id}
                        request={request}
                        records={work.records}
                        kinds={work.start.kinds}
                        onWork={onWork}
                        onOutcome={onOutcome}
                      />
                    </details>
                    {request.correctableLine && (
                      <details className="mt-2">
                        <summary className="cursor-pointer list-none text-[13px] font-medium text-ink hover:underline">
                          Correct the line
                        </summary>
                        <CorrectLine workId={work.id} request={request} onWork={onWork} onOutcome={onOutcome} />
                      </details>
                    )}
                    {request.mayDeclareNotAvailable && (
                      <details className="mt-2">
                        <summary className="cursor-pointer list-none text-[13px] font-medium text-ink hover:underline">
                          I don't have this
                        </summary>
                        <DeclareNotAvailable workId={work.id} request={request} onWork={onWork} onOutcome={onOutcome} />
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── CAN THESE RECORDS BE COMPARED AT ALL — an action, so it sits with the actions ── */}
          {work.comparability.some((row) => row.action !== null) && (
            <Section id="comparability">
              <SectionHead>Before the figures</SectionHead>
              <div className="flex flex-col gap-4">
                {work.comparability.filter((row) => row.action !== null).map((row) => (
                  <div key={row.dimension} className="border border-rule px-5 py-4">
                    <Label>{row.label}</Label>
                    <p className="mt-1.5 text-[15px]">
                      <StateText tone={markerTone(row.tone)}>{row.stateLabel}</StateText>
                    </p>
                    {row.action && <p className="mt-1.5 text-sm text-ink">{row.action}</p>}
                    <details className="mt-2">
                      <summary className="cursor-pointer list-none text-[13px] text-stone hover:text-ink">
                        What Leveri observed
                      </summary>
                      <p className="mt-1.5 text-[13px] text-stone">{row.grounds}</p>
                    </details>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── FOR YOU TO DECIDE, and preparing again with those decisions ── */}
          {preparation && <PreparationDecide work={work} preparation={preparation} onWork={onWork} onOutcome={onOutcome} />}

          {/* ── 3. YOUR REVIEW — every conclusion, one line each ──────────────
              The cause and the treatment stay visibly apart: explaining a
              difference and supporting a treatment are different acts. */}
          {work.conclusions.length > 0 && (
            <Section id="review">
              <SectionHead>Your review</SectionHead>
              <Conclusions title={cause.length > 0 && (treatment.length > 0 || others.length > 0) ? (work.causeHeading || "The difference") : null} rows={cause} {...reviewProps} />
              <Conclusions title={treatment.length > 0 && (cause.length > 0 || others.length > 0) ? "Accounting treatment" : null} rows={treatment} {...reviewProps} />
              <Conclusions title={classifications.length > 0 ? "Classifications" : null} rows={classifications} {...reviewProps} />
              <Conclusions title={mappings.length > 0 ? "Accounts to map" : null} rows={mappings} {...reviewProps} />
              <Conclusions title={others.length > 0 && (cause.length + treatment.length + classifications.length + mappings.length) > 0 ? "The records" : null} rows={others} {...reviewProps} />
              {/* A standing statement about the SYSTEM: a proposed conclusion is never a signed one. */}
              {work.review.sentence && (
                <p className="mt-4 text-[13px] text-stone" data-testid="review-sentence">{work.review.sentence}</p>
              )}
            </Section>
          )}

          {/* ADR-0018 — a model's proposed reading of what is still open. A
              proposal beside the treatment, never part of it. */}
          <ProposedReading work={work} onWork={onWork} onOutcome={onOutcome} />

          {/* ── 4. THE WORKING — for whoever checks it ────────────────────── */}
          {preparation && <PreparationBooks preparation={preparation} work={work} onWork={onWork} onOutcome={onOutcome} />}
          {work.bridge && (
            <Section id="bridge">
              <SectionHead>The reconciliation</SectionHead>
              {work.bridge.convention && <p className="mb-2 text-[13px] text-stone" data-testid="bridge-convention">{work.bridge.convention}</p>}
              <Figure first label={work.bridge.from.label} citation={work.bridge.from.citationText} value={work.bridge.from.text} total={false} />
              {work.bridge.items.map((item, index) => (
                <div key={`${item.label}-${index}`} data-testid="bridge-item">
                  <Figure label={item.lineLabel} citation={item.citationText} value={item.valueText} total={false} />
                  <p className="mt-1 text-[13px] text-stone">{item.reason}</p>
                  {item.assessment && (
                    <p className="mt-0.5 text-[13px]" title={item.assessment.band}>
                      <StateText tone={markerTone(item.assessment.tone)}>{item.assessment.label}</StateText>
                    </p>
                  )}
                </div>
              ))}
              <Figure label={work.bridge.to.label} citation={work.bridge.to.citationText} value={work.bridge.to.text} total />
              <p className="mt-3 text-[13px] text-stone" data-testid="bridge-sentence">{work.bridge.sentence}</p>
            </Section>
          )}

          {work.explanations.length > 0 && (
            <Section id="cause">
              <SectionHead>Where the difference is</SectionHead>
              {work.explanations.map((explanation, index) => (
                <Figure
                  key={`${explanation.reference ?? "unreferenced"}-${index}`}
                  first={index === 0}
                  label={
                    // Null is a finding, not a gap: when the record does not
                    // identify the transaction there is nothing to read back.
                    explanation.reference ?? <span className="text-stone">No reference in the record</span>
                  }
                  citation={explanation.locations.map((at) => `${at.sheetName} › row ${at.rowIndex}`).join(" · ")}
                  value={explanation.text}
                />
              ))}
              {work.explanations.some((e) => e.mechanisms.length > 0) && (
                <details className="mt-3">
                  <summary className="cursor-pointer list-none text-[13px] text-stone hover:text-ink">
                    How the difference was traced
                  </summary>
                  <div className="mt-2 flex flex-col gap-1.5">
                    {work.explanations.flatMap((explanation, index) =>
                      explanation.mechanisms.map((mechanism, at) => (
                        <Citation key={`${index}-${mechanism}`} as="div" title={mechanism}>
                          {explanation.mechanismLabels[at] ?? mechanism}
                        </Citation>
                      ))
                    )}
                  </div>
                </details>
              )}
            </Section>
          )}

          {/* ── WHAT HAS BEEN ESTABLISHED — the facts behind every line above,
              one click away rather than repeated in full on the page. ── */}
          <Section id="established">
            <details>
              <summary className="cursor-pointer list-none text-[15px] font-semibold text-ink hover:underline">
                How Leveri established it <span className="ml-1 text-[13px] font-normal text-stone">{work.established.length}</span>
              </summary>
              {work.established.length === 0 ? (
                <p className="mt-3 text-sm text-stone">Nothing has been established on this work yet.</p>
              ) : (
                <RuledList className="mt-3">
                  {work.established.map((item) => (
                    <div key={item.id} className="py-3">
                      {/* `reading` is the same sentence made readable on the server. */}
                      <p className="text-[15px] text-ink">{item.plain ?? item.reading ?? item.statement}</p>
                      <Citation as="div" className="mt-1" title={`${item.factKind} · ${item.derivation}`}>
                        {item.factKindLabel ? `${item.factKindLabel} · ` : ""}{item.derivationLabel || item.derivation}
                      </Citation>
                    </div>
                  ))}
                </RuledList>
              )}
            </details>
          </Section>

          {/* What Leveri understood the work to be — context, kept, below the work itself. */}
          <IntentSection work={work} onWork={onWork} onOutcome={onOutcome} />

          {/* WHERE THE EARLIER READING WENT (R5.1): kept in full under History. */}
          {(work.superseded.established.length > 0 || work.superseded.conclusions.length > 0) && (
            <p className="mt-8 text-[13px] text-stone">
              An earlier reading of this work was superseded when it resumed. It is kept in full under History.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * A governed conclusion: the question as the engine asked it, the conclusion
 * as it committed it, and the governance status it recorded. The tone is the
 * server's; the words are the server's; the screen adds none of its own.
 */
function Conclusions({ title, rows, work, reviewing, onReviewing, onWork, onOutcome }: {
  title: string | null;
  rows: readonly ConclusionRow[];
  work: WorkFile;
  reviewing: string | null;
  onReviewing: (id: string | null) => void;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}) {
  if (rows.length === 0) return null;
  // A label that every row shares distinguishes nothing ("How should these be
  // classified?" on every line): then the question's own words are the line.
  const sharedLabel = rows.length > 1 && rows.every((r) => r.questionLabel && r.questionLabel === rows[0]!.questionLabel);
  return (
    <div className="mb-4">
      {title && <Label className="mb-1 mt-2">{title}</Label>}
      <RuledList>
        {rows.map((row) => (
          <div key={row.id} className="py-3.5">
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-medium text-ink">{sharedLabel ? row.questionText : row.questionLabel || row.questionText}</p>
                <p className="mt-1 text-sm"><StateText tone={markerTone(row.tone)}>{row.conclusionPlain ?? row.conclusionReading ?? row.conclusionText}</StateText></p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                <StateText tone={markerTone(row.review.tone)}>{row.review.label}</StateText>
                {(row.governanceLabel || row.governanceStatus) !== row.review.label && (
                  <span className="text-[12px] text-stone" title={row.governanceStatus}>{row.governanceLabel || row.governanceStatus}</span>
                )}
              </div>
            </div>
            {/* The engine's own question, kept — it is more precise than the short form. */}
            {!sharedLabel && row.questionLabel && row.questionLabel !== row.questionText && (
              <details className="mt-1">
                <summary className="cursor-pointer list-none text-[13px] text-stone hover:text-ink">
                  The question as Leveri asked it
                </summary>
                <p className="mt-1 text-[13px] text-stone">{row.questionText}</p>
              </details>
            )}
            <ReviewPosition row={row} />
            <ReviewControl work={work} row={row} open={reviewing === row.id} onOpen={onReviewing} onWork={onWork} onOutcome={onOutcome} />
          </div>
        ))}
      </RuledList>
    </div>
  );
}
